import type { VoiceFlowSessionBootstrap, VoicePendingConfirmation } from 'types';

export interface RecoverableVoiceDraft {
  sessionId: number;
  runtimeGeneration: number;
  questionId: number | null;
  contentId: string;
  targetRevision?: string;
  transcript: string;
  inputAudioItemId: string;
  speechGroupId?: string;
  confirmation?: VoicePendingConfirmation | null;
  savedAt: number;
  finalized?: boolean;
  transcriptionModel?: string;
  capturedAt?: string;
  retryable?: boolean;
  recoveryAttempts?: number;
}

const PREFIX = 'wisefriend:voice-draft:';
const MAX_AGE_MS = 30 * 60 * 1000;

// Session storage survives route remounts but does not retain reflection text
// after the browser session. Audio is never stored here.
export const storeVoiceDraft = (draft: RecoverableVoiceDraft): void => {
  if (!draft.transcript.trim()) return;
  try {
    const key = `${PREFIX}${draft.sessionId}`;
    const raw = window.sessionStorage.getItem(key);
    const saved = raw ? JSON.parse(raw) as RecoverableVoiceDraft | RecoverableVoiceDraft[] : [];
    const drafts = Array.isArray(saved) ? saved : [saved];
    const index = drafts.findIndex((value) => value.inputAudioItemId === draft.inputAudioItemId);
    if (index >= 0) drafts[index] = { ...drafts[index], ...Object.fromEntries(Object.entries(draft).filter(([, value]) => value !== undefined)) } as RecoverableVoiceDraft;
    else drafts.push(draft);
    window.sessionStorage.setItem(key, JSON.stringify(drafts.slice(-8)));
  } catch { /* unavailable storage */ }
};

export const clearVoiceDraft = (sessionId: number, inputAudioItemId?: string): void => {
  try {
    const key = `${PREFIX}${sessionId}`;
    if (inputAudioItemId) {
      const value = window.sessionStorage.getItem(key);
      if (value) {
        const saved = JSON.parse(value) as RecoverableVoiceDraft | RecoverableVoiceDraft[];
        const drafts = (Array.isArray(saved) ? saved : [saved]).filter((draft) => draft.inputAudioItemId !== inputAudioItemId);
        if (drafts.length) { window.sessionStorage.setItem(key, JSON.stringify(drafts)); return; }
      }
    }
    window.sessionStorage.removeItem(key);
  } catch { /* unavailable storage */ }
};

export const clearIncompleteVoiceDrafts = (sessionId: number, questionId: number | null): void => {
  try {
    const key = `${PREFIX}${sessionId}`;
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return;
    const saved = JSON.parse(raw) as RecoverableVoiceDraft | RecoverableVoiceDraft[];
    const drafts = (Array.isArray(saved) ? saved : [saved]).filter((draft) => (
      draft.questionId !== questionId || draft.finalized !== false
    ));
    if (drafts.length) window.sessionStorage.setItem(key, JSON.stringify(drafts));
    else window.sessionStorage.removeItem(key);
  } catch { /* unavailable storage */ }
};

export const restoreVoiceDraft = (session: VoiceFlowSessionBootstrap): RecoverableVoiceDraft | null => {
  try {
    const raw = window.sessionStorage.getItem(`${PREFIX}${session.id}`);
    if (!raw) return null;
    const saved = JSON.parse(raw) as RecoverableVoiceDraft | RecoverableVoiceDraft[];
    const drafts = Array.isArray(saved) ? saved : [saved];
    const currentQuestionId = session.current_question?.id ?? null;
    const valid = drafts.filter((draft) => {
      const knownQuestion = session.content_schema.some((item) => item.question_id === draft.questionId);
      return !(draft.sessionId !== session.id || draft.runtimeGeneration !== (session.runtime_generation ?? 0)
      || typeof draft.transcript !== 'string' || !draft.transcript.trim()
      || typeof draft.savedAt !== 'number' || Date.now() - draft.savedAt > MAX_AGE_MS
      || (draft.questionId !== currentQuestionId && !knownQuestion)
      || session.status === 'closed' || session.status === 'crisis_paused'
      || (draft.transcriptionModel && draft.transcriptionModel !== session.transcription_model)
      || (draft.confirmation && (draft.confirmation.id !== session.pending_confirmation?.id
        || draft.confirmation.revision !== session.pending_confirmation?.revision)));
    });
    if (!valid.length) {
      clearVoiceDraft(session.id);
      return null;
    }
    window.sessionStorage.setItem(`${PREFIX}${session.id}`, JSON.stringify(valid));
    // Keep words from an older follow-up visible, but never retry them as an
    // answer to the newer same-question prompt. Legacy unversioned questions
    // retain their existing recovery behavior.
    const currentRevision = session.current_target?.target_revision ?? undefined;
    const recoverable = valid.map((draft) => draft.questionId === currentQuestionId
      && draft.targetRevision !== currentRevision ? { ...draft, retryable: false } : draft);
    // Confirmation belongs to the capture, never to a newer resumed proposal.
    return recoverable.find((draft) => draft.finalized !== false && draft.retryable !== false) ?? recoverable[0];
  } catch { return null; }
};
